import React, { useMemo, useReducer } from "react";
import { FullscreenTakeover, StepIndicator } from "@jsq/ds/core";
import {
  createPayeeReducer,
  initialCreatePayeeState,
  CreateBasePayeeReducerContextProvider,
  CreatePayeeReducerDispatchProvider,
  createPayeeStepReducer,
  initialCreatePayeeStepState,
  CreatePayeeStepContextProvider,
  CreatePayeeStepReducerDispatchProvider,
} from "../Context/CreatePayeeContext";
import { fullScreenTakeOverBodyStyles } from "../styles_vanilla.css";
import { CreatePayeeWorkflowProps, CreatePayeeWorkflowStep } from "../types";
import { StepNames } from "./constants";
import { CreateEntityClientDetailsAndSupportingDocumentStep } from "./StepComponents/CreateEntityClientDetailsAndSupportingDocumentStep";
import { CreateEntityPayeeDetailsStep } from "./StepComponents/CreateEntityPayeeDetailsStep";
import { CreateEntityPaymentInstructionsStep } from "./StepComponents/CreateEntityPaymentInstructionsStep";
import { CreateEntityReviewStep } from "./StepComponents/CreateEntityReviewStep";

export const CreatePayeeEntityWorkflow: React.FC<CreatePayeeWorkflowProps> = ({
  onExit,
  initialState = initialCreatePayeeState,
}: CreatePayeeWorkflowProps) => {
  const [payeeData, dispatch] = useReducer(createPayeeReducer, initialState);
  const [step, dispatchStep] = useReducer(createPayeeStepReducer, initialCreatePayeeStepState);

  const workflowSteps: CreatePayeeWorkflowStep[] = useMemo(() => {
    return [
      {
        index: 0,
        name: StepNames.ClientDetailsAndSupportingDocument,
        url: "0",
        status: step.stepNumber === 0 ? "active" : "inactive",
        component: CreateEntityClientDetailsAndSupportingDocumentStep,
      },
      {
        index: 1,
        name: StepNames.PayeeDetails,
        url: "1",
        status: step.stepNumber === 1 ? "active" : "inactive",
        component: CreateEntityPayeeDetailsStep,
      },
      {
        index: 2,
        name: StepNames.PaymentInstructions,
        url: "2",
        status: step.stepNumber === 2 ? "active" : "inactive",
        component: CreateEntityPaymentInstructionsStep,
      },
      {
        index: 3,
        name: StepNames.Review,
        url: "3",
        status: step.stepNumber === 3 ? "active" : "inactive",
        component: CreateEntityReviewStep,
      },
    ];
  }, [step]);

  const currentStepDetails = useMemo(
    () => workflowSteps[step.stepNumber],
    [step, workflowSteps],
  );

  const StepComponent = currentStepDetails.component;

  return (
    <CreateBasePayeeReducerContextProvider value={payeeData}>
      <CreatePayeeReducerDispatchProvider value={dispatch}>
        <CreatePayeeStepContextProvider value={step}>
          <CreatePayeeStepReducerDispatchProvider value={dispatchStep}>
            <FullscreenTakeover>
              <FullscreenTakeover.Header title={"Create new payee - Entity"} onExit={onExit} />
              <FullscreenTakeover.Subheader>
                <StepIndicator disableStepLinks steps={workflowSteps} />
              </FullscreenTakeover.Subheader>
              <FullscreenTakeover.Body className={fullScreenTakeOverBodyStyles}>
                <StepComponent />
              </FullscreenTakeover.Body>
            </FullscreenTakeover>
          </CreatePayeeStepReducerDispatchProvider>
        </CreatePayeeStepContextProvider>
      </CreatePayeeReducerDispatchProvider>
    </CreateBasePayeeReducerContextProvider>
  );
};

export default CreatePayeeEntityWorkflow;
